import {gql} from '@apollo/client';

import {NucleotideType} from 'biojs/BioConstants';
import {Sequence as BioJSSequence} from 'biojs/Sequence';
import {Sequence} from 'components/Sequence';
import {SEQUENCE_TYPE_GQL_TO_NATIVE_ENUM_MAP, SequenceServerResponse} from 'constants/SequenceConstants';
import {DisplaySequenceFileState} from 'FileStates';
import {SequenceType as GQLSequenceType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import deepCopy from 'lib/deepCopy';

interface SequenceContentsQuerySequence {
  id: string;
  name: string;
  sequenceType: GQLSequenceType;
  nucleotideType: NucleotideType;
}

interface SequenceContentsQueryVersion {
  dbId: number;
  bases: string;
  circular: boolean;
  startIndex: number;
  annotationsJson: JSONScalar;
  partsJson: JSONScalar;
  sequencePrimersJson: JSONScalar;
  primerPairsJson: JSONScalar;
  translationGroupsJson: JSONScalar;
  translationsJson: JSONScalar;
  modifiedNucleotidesJson: JSONScalar;
}

export function getBaseCoercionWarningText(nucleotideType: NucleotideType): string {
  const wrongBase = nucleotideType === NucleotideType.DNA ? 'U' : 'T';
  const rightBase = nucleotideType === NucleotideType.DNA ? 'T' : 'U';
  return `You're working with ${nucleotideType}, so we changed ${wrongBase} to ${rightBase}.`;
}

export const SEQUENCE_CONTENTS_QUERY = gql`
  query SequenceContentsQuery($id: StrictString!, $versionDbId: StrictInt!)
  @skipAudit
  @engComponent(name: MOLBIO) {
    sequence(id: $id) {
      ... on SequenceNode {
        id
        name
        sequenceType
        nucleotideType
        version(dbId: $versionDbId) {
          ... on SequenceVersionNode {
            dbId
            bases
            circular
            startIndex
            annotationsJson
            partsJson
            sequencePrimersJson
            primerPairsJson
            translationGroupsJson
            translationsJson
            modifiedNucleotidesJson
          }
          ...InaccessibleInfoGraphQL
        }
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

function sequenceGqlToServerResponse(
  sequenceContents: SequenceContentsQuerySequence,
  sequenceVersionContents: SequenceContentsQueryVersion
): Partial<SequenceServerResponse> {
  return {
    id: sequenceContents.id,
    name: sequenceContents.name,
    nucleotideType: sequenceContents.nucleotideType,
    bases: sequenceVersionContents.bases,
    circular: sequenceVersionContents.circular,
    startIndex: sequenceVersionContents.startIndex,
    type: SEQUENCE_TYPE_GQL_TO_NATIVE_ENUM_MAP[sequenceContents.sequenceType],
    annotations: sequenceVersionContents.annotationsJson,
    parts: sequenceVersionContents.partsJson,
    sequence_primers: sequenceVersionContents.sequencePrimersJson,
    primer_pairs: sequenceVersionContents.primerPairsJson,
    translations: sequenceVersionContents.translationsJson,
    translationGroups: sequenceVersionContents.translationGroupsJson,
    modified_nucleotides: sequenceVersionContents.modifiedNucleotidesJson,
  };
}

/**
 * Function to build a read-only Sequence Backbone model. Expects the output from SequenceContentsQuery.
 * @param sequenceContents Sequence output from SequenceContentsQuery
 * @param sequenceVersionContents Sequence version output from SequenceContentsQuery
 * @param complexPolymerId Optional API ID of the complex polymer the sequence may be rendered in
 * @returns Sequence Backbone model
 */
export function buildSequenceModel(
  sequenceContents: SequenceContentsQuerySequence,
  sequenceVersionContents: SequenceContentsQueryVersion,
  complexPolymerId?: string
): Sequence {
  // GQL responses are secretly readonly, and Sequence parsing tries to mutate the object so we need
  // to perform a deep copy.
  const rawBiojsSequenceAttributes = deepCopy(
    sequenceGqlToServerResponse(sequenceContents, sequenceVersionContents)
  );
  const biojsSequence = new BioJSSequence(
    {
      ...rawBiojsSequenceAttributes,
      finalized: true,
      complexPolymerId,
    },
    {parse: true}
  );
  const sequence = new Sequence({
    id: sequenceContents.id,
    _sequence: biojsSequence,
    fileState: null,
  });
  const fileState = new DisplaySequenceFileState({sequence});
  sequence.set({fileState});
  return sequence;
}
